import React, { ChangeEvent, RefObject, useRef } from 'react'

import { HiOutlinePaperClip } from 'react-icons/hi'

import { Button, Text } from '@chakra-ui/react'

import {
  ColorScheme,
  Button as DarkButton,
  PaperClipIcon,
} from '@hivewatch/dark'

import { ALLOWED_FILE_EXTENSIONS } from '../utils/constants'

interface AttachFilesButtonIProps {
  allowedExtensions?: string
  handleFileSelect: (event: ChangeEvent<HTMLInputElement>) => void
  maxFiles: number
  selectedFiles: File[]
  /**
   * Rebrand: renders the design-system button instead of the legacy outline
   * one, matching the Attach control every other create modal uses. Omit to
   * keep the legacy appearance untouched.
   */
  colorScheme?: ColorScheme
}

const getButtonLabel = (count: number, max: number): string => {
  return count === 0 ? 'Attach Files' : `${count}/${max} files selected`
}

export const AttachFilesButton = ({
  allowedExtensions = ALLOWED_FILE_EXTENSIONS.join(','),
  handleFileSelect,
  maxFiles,
  selectedFiles,
  colorScheme,
}: AttachFilesButtonIProps) => {
  const fileInputRef: RefObject<HTMLInputElement> = useRef()
  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const isDisabled = selectedFiles.length >= maxFiles
  const label = getButtonLabel(selectedFiles.length, maxFiles)

  return (
    <>
      {colorScheme ? (
        <DarkButton
          colorScheme={colorScheme}
          data-testid='createIncidentModal_attachFilesButton'
          disabled={isDisabled}
          leftIcon={<PaperClipIcon colorScheme={colorScheme} size={18} />}
          onClick={openFileDialog}
          style={{ width: '100%' }}
          variant='tertiary'
        >
          {label}
        </DarkButton>
      ) : (
        <Button
          _hover={{ borderColor: '#9FB8E5' }}
          borderColor='#9FB8E5'
          borderRadius='md'
          borderWidth='2px'
          data-testid='createIncidentModal_attachFilesButton'
          height='10'
          isDisabled={isDisabled}
          leftIcon={<HiOutlinePaperClip size={18} />}
          onClick={openFileDialog}
          variant='outline'
          width='full'
        >
          <Text fontSize='sm' fontWeight='500'>
            {label}
          </Text>
        </Button>
      )}
      <input
        accept={allowedExtensions}
        aria-label='Select files to attach'
        className='hidden'
        data-testid='createIncidentModal_fileInput'
        multiple
        onChange={handleFileSelect}
        onClick={(event) => {
          if (event.currentTarget) {
            event.currentTarget.value = ''
          }
        }}
        ref={fileInputRef}
        type='file'
      />
    </>
  )
}
